import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { WS_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SocketCtx = createContext(null);

export function SocketProvider({ children }) {
  const { token, user } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const listenersRef = useRef(new Set());

  const on = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  useEffect(() => {
    if (!token) {
      wsRef.current?.close();
      return;
    }
    let alive = true;
    let retry = 0;
    function open() {
      if (!alive) return;
      const ws = new WebSocket(`${WS_BASE}/api/ws/${token}`);
      wsRef.current = ws;
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
      };
      ws.onclose = () => {
        setConnected(false);
        if (alive) {
          retry = Math.min(retry + 1, 5);
          setTimeout(open, 800 * retry);
        }
      };
      ws.onerror = () => {};
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          listenersRef.current.forEach((l) => l(msg));
          // Global toasts for key events
          handleGlobalEvent(msg, user);
        } catch {}
      };
    }
    open();
    // keepalive
    const ping = setInterval(() => {
      try {
        wsRef.current?.readyState === 1 &&
          wsRef.current.send(JSON.stringify({ event: "ping" }));
      } catch {}
    }, 25000);
    return () => {
      alive = false;
      clearInterval(ping);
      wsRef.current?.close();
    };
  }, [token, user]);

  return (
    <SocketCtx.Provider value={{ connected, on }}>{children}</SocketCtx.Provider>
  );
}

function handleGlobalEvent(msg, user) {
  if (!msg || !msg.event) return;
  switch (msg.event) {
    case "new_job":
      toast.success("New job nearby!", {
        description: `${msg.data.sport} · $${msg.data.hourly_rate}/hr`,
      });
      break;
    case "booking_accepted":
      toast.success("A ball boy accepted your booking!", {
        description: msg.data?.ballboy?.name,
      });
      break;
    case "booking_status":
      toast("Booking update", {
        description: `Status: ${msg.data.status}${
          msg.data.eta_minutes != null ? ` · ETA ${msg.data.eta_minutes} min` : ""
        }`,
      });
      break;
    case "chat_message": {
      const m = msg.data?.message;
      if (m && m.sender_id !== user?.id) {
        toast(`${m.sender_name}`, { description: m.text });
      }
      break;
    }
    case "payment_confirmed":
      toast.success("Payment confirmed", { description: `$${msg.data.amount}` });
      break;
    case "payment_received":
      toast.success("Payment received", { description: `$${msg.data.amount}` });
      break;
    case "verification_decided":
      toast(`Verification ${msg.data.status}`, {
        description: msg.data.reason || "",
      });
      break;
    case "verification_submitted":
      toast("New ball boy verification submitted");
      break;
    case "new_rating":
      toast("You got a new rating", {
        description: `${msg.data.stars} stars`,
      });
      break;
    default:
      break;
  }
}

export function useSocket() {
  return useContext(SocketCtx);
}
